import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { useBranding } from '../contexts/BrandingContext';

function AdminManagePricing() {
  const { branding } = useBranding();
  const primaryColor = branding?.primaryColor || '#667eea';
  const token = localStorage.getItem('adminToken');
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedRule, setSelectedRule] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [timeSlots, setTimeSlots] = useState({
    weekday: {
      '06:00-11:00': 0,
      '11:00-16:00': 0,
      '16:00-18:00': 0,
      '18:00-22:00': 0
    },
    weekend: {
      '06:00-18:00': 0,
      '18:00-22:00': 0
    }
  });

  useEffect(() => {
    fetchPricingRules();
  }, []);

  const fetchPricingRules = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/pricing`);
      const data = await response.json();
      setPricingRules(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load pricing rules');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setTimeSlots(rule.time_slots || {
      weekday: {
        '06:00-11:00': 0,
        '11:00-16:00': 0,
        '16:00-18:00': 0,
        '18:00-22:00': 0
      },
      weekend: {
        '06:00-18:00': 0,
        '18:00-22:00': 0
      }
    });
    setEditMode(true);
  };

  const handleSlotPriceChange = (dayType, slot, value) => {
    setTimeSlots(prev => ({
      ...prev,
      [dayType]: {
        ...prev[dayType],
        [slot]: parseFloat(value) || 0
      }
    }));
  };

  const handleSavePricing = async () => {
    if (!selectedRule) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/pricing/${selectedRule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ time_slots: timeSlots })
      });

      if (!response.ok) throw new Error('Failed to update pricing');

      setSuccess('Pricing updated successfully!');
      setEditMode(false);
      setSelectedRule(null);
      fetchPricingRules();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayPrice = (rule) => {
    const weekdayMorning = rule.time_slots?.weekday?.['06:00-11:00'];
    const weekdayAfternoon = rule.time_slots?.weekday?.['11:00-16:00'];
    return weekdayMorning || weekdayAfternoon || rule.base_price || 0;
  };

  if (loading && pricingRules.length === 0) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading pricing...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {success && <div className="admin-alert success">{success}</div>}
      {error && <div className="admin-alert error">{error}</div>}

      {/* Pricing Rules List */}
      {!editMode && (
        <div className="admin-card">
          <div className="admin-card-header">Court Pricing Rules ({pricingRules.length})</div>

          {pricingRules.length === 0 ? (
            <div className="admin-empty">
              <div className="admin-empty-icon">💰</div>
              <p>No pricing rules found</p>
            </div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Court</th>
                    <th>Sport</th>
                    <th>Type</th>
                    <th>Base Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingRules.map((rule) => (
                    <tr key={rule.id}>
                      <td style={{ fontWeight: 600 }}>{rule.court_name || '-'}</td>
                      <td>{rule.sport_name || '-'}</td>
                      <td>
                        <span className="admin-badge admin-badge-info">{rule.pricing_type}</span>
                      </td>
                      <td style={{ fontWeight: 600, color: primaryColor }}>
                        ₹{parseFloat(getDisplayPrice(rule)).toFixed(0)}
                      </td>
                      <td>
                        <span className={`admin-badge ${rule.is_active ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => handleEditRule(rule)} className="admin-btn admin-btn-primary admin-btn-sm">
                          Edit Pricing
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Time Slot Editor */}
      {editMode && selectedRule && (
        <div className="admin-card">
          <div className="admin-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Edit Pricing: {selectedRule.court_name}</span>
            <button onClick={() => { setEditMode(false); setSelectedRule(null); }} className="admin-btn admin-btn-secondary admin-btn-sm">
              ← Back to List
            </button>
          </div>
          <div className="admin-card-body">
            <p style={{ color: '#6b7280', marginBottom: '20px' }}>Sport: {selectedRule.sport_name}</p>

            {/* Weekday Pricing */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px', paddingBottom: '8px', borderBottom: `2px solid ${primaryColor}` }}>
                Weekday Pricing (Mon-Fri)
              </h4>
              <div className="admin-form-row">
                {Object.entries(timeSlots.weekday).map(([slot, price]) => (
                  <div key={slot} className="admin-field">
                    <label className="admin-label">{slot}</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>₹</span>
                      <input
                        type="number"
                        value={price || ''}
                        onChange={(e) => handleSlotPriceChange('weekday', slot, e.target.value)}
                        min="0"
                        step="10"
                        placeholder="0"
                        className="admin-input"
                        style={{ paddingLeft: '28px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Weekend Pricing */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #10b981' }}>
                Weekend Pricing (Sat-Sun)
              </h4>
              <div className="admin-form-row" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                {Object.entries(timeSlots.weekend).map(([slot, price]) => (
                  <div key={slot} className="admin-field">
                    <label className="admin-label">{slot}</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>₹</span>
                      <input
                        type="number"
                        value={price || ''}
                        onChange={(e) => handleSlotPriceChange('weekend', slot, e.target.value)}
                        min="0"
                        step="10"
                        placeholder="0"
                        className="admin-input"
                        style={{ paddingLeft: '28px' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Outdoor Note */}
            {selectedRule.court_type === 'outdoor' && (
              <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', marginBottom: '20px' }}>
                <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                  <strong>Note:</strong> This is an outdoor court. Night slots (18:00-22:00) should include ₹50 light charges.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button onClick={() => { setEditMode(false); setSelectedRule(null); }} className="admin-btn admin-btn-secondary">
                Cancel
              </button>
              <button onClick={handleSavePricing} disabled={loading} className="admin-btn admin-btn-primary">
                {loading ? 'Saving...' : 'Save Pricing Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminManagePricing;
